<template>
  <span class="loader" v-if="loading"></span>
  <div v-else>
    <div v-if="showLoaderPopup" class="loader-modal">
      <span class="loader"></span>
    </div>

    <h1 class="title">
      <template v-if="basket.type === 'Bestelling'">{{ $t('order') }}</template>
      <template v-else>{{ $t('quote') }}</template>
      <span v-if="this.isAdmin" style="color: red"> [ADMIN]</span></h1>
    <div class="step-details">
      <h3>{{ $t('deliveryForm.title') }}</h3>
      <span class="line"/>
      <span class="step">{{ $t('step') }} {{ getTotalSteps() }}</span>
    </div>

    <ErrorBox v-if="form.errors && Object.keys(form.errors).length > 0 && !form.firstVisit"
              :errors="form.errors"></ErrorBox>

    <div class="blocks">
      <div class="block-steps">
        <span class="step">{{ $t('step') }} {{ getTotalSteps() }}</span>
        <span class="line-vertical"/>
      </div>
      <div class="block-form">
        <h3 class="desktop">{{ $t('deliveryForm.title') }}</h3>

        <div class="grouped-radio-inputs">
          <RadioInput @change="form.sameAsInvoice = 0; updateForm('delivery', true)"
                      :input-value="0"
                      :model-value="form.sameAsInvoice"
                      :label-text="$t('deliveryForm.differentAddress')"
                      label-for="different_delivery"
          ></RadioInput>
          <RadioInput @change="form.sameAsInvoice = 1; updateForm('delivery', true)"
                      :input-value="1"
                      :model-value="form.sameAsInvoice"
                      :label-text="$t('deliveryForm.sameAddress')"
                      label-for="same_delivery"
          ></RadioInput>
          <RadioInput v-if="basket.isAdmin"
                      @change="form.sameAsInvoice = 2; updateForm('delivery', true)"
                      :input-value="2"
                      :model-value="form.sameAsInvoice"
                      :label-text="$t('deliveryForm.pickupHapert')"
                      label-for="admin_pickup"
          ></RadioInput>
        </div>

        <template v-if="form.sameAsInvoice === 0">
          <TextInput @change="updateInputValue('location', $event.value)" :input-value="form.location"
                     labelFor="location" :labelText="$t('location')"
                     :show-error="!form.firstVisit" :error-text="form.errors?.location"
                     :max-length="form.modelLengths?.location" required
          ></TextInput>

          <SelectInput @change="updateInputValue('country', $event.value)"
                       :options="selectCountries" :select-value="form.country"
                       :label-text="$t('country')" label-for="country"
                       :show-error="!form.firstVisit" :error-text="form.errors?.country"
                       required
          >
            <template v-slot:extraOptions>
              <option value="">{{ $t("optionSelect") }}...</option>
            </template>
          </SelectInput>

          <template v-if="form.reversedAddress">
            <div class="double-input">
              <TextInput @change="updateInputValue('houseNumber', $event.value)" :inputValue="form.houseNumber"
                         labelFor="houseNumber" :labelText="$t('houseNumber')"
                         :show-error="!form.firstVisit" :error-text="form.errors?.houseNumber"
                         :max-length="form.modelLengths?.houseNumber" smallInput
              ></TextInput>
              <TextInput @change="updateInputValue('streetName', $event.value)" :inputValue="form.streetName"
                         labelFor="streetName" :labelText="$t('streetName')"
                         :show-error="!form.firstVisit" :error-text="form.errors?.streetName"
                         :max-length="form.modelLengths?.streetName" required
              ></TextInput>
            </div>
          </template>
          <template v-else>
            <TextInput @change="updateInputValue('streetName', $event.value)" :inputValue="form.streetName"
                       labelFor="streetName" :labelText="$t('streetName')"
                       :show-error="!form.firstVisit" :error-text="form.errors?.streetName"
                       :max-length="form.modelLengths?.streetName" required
            ></TextInput>
            <div class="double-input">
              <TextInput @change="updateInputValue('houseNumber', $event.value)" :inputValue="form.houseNumber"
                         labelFor="houseNumber" :labelText="$t('houseNumber')"
                         :show-error="!form.firstVisit" :error-text="form.errors?.houseNumber"
                         :max-length="form.modelLengths?.houseNumber"
              ></TextInput>
              <TextInput @change="updateInputValue('extension', $event.value)" :inputValue="form.extension"
                         labelFor="extension" :labelText="$t('extension')"
                         :show-error="!form.firstVisit" :error-text="form.errors?.extension"
                         :max-length="form.modelLengths?.extension"
              ></TextInput>
            </div>
          </template>

          <TextInput @change="updateInputValue('zipcode', $event.value)" :inputValue="form.zipcode"
                     labelFor="zipcode" :labelText="$t('zipcode')"
                     :show-error="!form.firstVisit" :error-text="form.errors?.zipcode"
                     :max-length="form.modelLengths?.zipcode" required
          ></TextInput>

          <TextInput @change="updateInputValue('city', $event.value)" :inputValue="form.city"
                     labelFor="city" :labelText="$t('city')"
                     :show-error="!form.firstVisit" :error-text="form.errors?.city"
                     :max-length="form.modelLengths?.city" required
          ></TextInput>
        </template>

        <TextInput @change="updateInputValue('contactName', $event.value)" :inputValue="form.contactName"
                   labelFor="contactName" :labelText="$t('contactName')"
                   :show-error="!form.firstVisit" :error-text="form.errors?.contactName"
                   :max-length="form.modelLengths?.contactName"
        ></TextInput>

        <TextInput @change="updateInputValue('contactPhone', $event.value)" :inputValue="form.contactPhone"
                   labelFor="contactPhone" :labelText="$t('contactPhone')"
                   :show-error="!form.firstVisit" :error-text="form.errors?.contactPhone"
                   :max-length="form.modelLengths?.contactPhone"
        ></TextInput>

        <TextInput @change="updateInputValue('contactCellphone', $event.value)" :inputValue="form.contactCellphone"
                   labelFor="contactCellphone" :labelText="$t('contactCellphone')"
                   :show-error="!form.firstVisit" :error-text="form.errors?.contactCellphone"
                   :max-length="form.modelLengths?.contactCellphone"
        ></TextInput>

        <BasketSummary v-if="basket.isCustom" :type="basket.type" :custom-rows="basket.customRows"
                       :valuta="basket.valuta" :total-excl="basket.customExcl" :total-incl="basket.customIncl"
                       :vat-percentage="basket.vatPercentage" :total-vat="basket.customVat"></BasketSummary>
        <BasketSummary v-else :type="basket.type" :contact-form="contactForm" :products="basket.products"
                       :total-excl="basket.totalExcl"
                       :vat-percentage="basket.vatPercentage"
                       :total-vat="basket.totalVat"
                       :kwantum-korting-excl="basket.kwantumKortingExcl" :dealer-row="basket.dealerRow"
                       :dealer-amount="basket.dealerAmount"
                       :totalIncl="basket.totalIncl" :shipping-costs="basket.shippingCostsExcl"></BasketSummary>
        <br>
        <template v-if="!this.hasPptMove">
          <FormInput :label-text="$t('questionRemark')" label-for="remark">
            <textarea @change="updateForm('delivery')"
                      rows="5" id="remark" type="text"
                      v-model="form.remark" :maxlength="form.modelLengths?.remark"
            ></textarea>
          </FormInput>

          <div class="form-input checkbox">
            <input @change="updateForm('delivery')" type="checkbox" id="terms" v-model="form.terms"
                   :class="{ 'error' : (!this.hasPptMove && !form.terms && !form.firstVisit) }">
            <label for="terms"
                   :class="{ 'error' : (!this.hasPptMove && !form.terms && !form.firstVisit) }">{{ $t('iAccept') }}
              <a :href="termsHref"
                 target="_blank">{{ $t('acceptTerms') }}</a>{{ $t('andThe') }}<a
                  :href="deliveryHref"
                  target="_blank">{{ $t('acceptDelivery') }}</a>.</label>
          </div>
          <div class="form-input checkbox">
            <input @change="updateForm('delivery')" type="checkbox" id="newsletter" v-model="form.newsletter">
            <label for="newsletter">{{ $t('newsletter') }}</label>
          </div>
        </template>

        <div v-if="isAdmin" class="form-input checkbox">
          <input @change="updateForm('delivery')" type="checkbox" id="noMailCustomer" v-model="form.noMailCustomer">
          <label for="noMailCustomer">Klant niet mailen</label>
        </div>

        <div class="error-notification" v-if="form.errors && Object.keys(form.errors).length > 0 && !form.firstVisit">
          <span>
          {{ $t('errorTitle') }}
          </span>
        </div>

        <div class="nav-btns">
          <button v-if="hasPptMove" style="margin-bottom: 8px" :disabled="disableNextStep"
                  @click="nextStep('pickup-details')"
                  class="gsd-btn gsd-btn-secondary">{{ $t('deliveryForm.toPickup') }}
          </button>
          <div class="btn-split" :style="{'flex-direction' : basket.type == 'Offerte' ? 'row-reverse' : ''}" v-else>
            <button :disabled="disableNextStep" @click="setType('Offerte'); placeOrder()"
                    class="gsd-btn"
                    :class="{'gsd-btn-secondary' : basket.type == 'Offerte', 'gsd-btn-tertiary' : basket.type == 'Bestelling'}">
              {{ $t('requestQuote') }}
            </button>
            <button :disabled="disableNextStep" @click="setType('Bestelling'); nextStep('payment-details')"
                    class="gsd-btn"
                    :class="{'gsd-btn-tertiary' : basket.type == 'Offerte', 'gsd-btn-secondary' : basket.type == 'Bestelling'}">
              {{ $t('deliveryForm.toPaymentOptions') }}
            </button>
          </div>
          <router-link :to="{ name: 'contact-details' }" class="gsd-btn gsd-btn-tertiary">
            <svg xmlns="http://www.w3.org/2000/svg" height="1.2em" viewBox="0 0 512 512" fill="currentcolor">
              <path
                  d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288 480 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-370.7 0 73.4-73.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-128 128z"/>
            </svg>
            {{ $t('back') }}
          </router-link>
        </div>
      </div>

      <div class="block-right">
        <BasketSummary v-if="basket.isCustom" :type="basket.type" :custom-rows="basket.customRows"
                       :valuta="basket.valuta" :total-excl="basket.customExcl" :total-incl="basket.customIncl"
                       :vat-percentage="basket.vatPercentage" :total-vat="basket.customVat"
                       :is-pickup="hasPptMove" :show-shipping="true"></BasketSummary>
        <BasketSummary v-else :type="basket.type" :contact-form="contactForm" :products="basket.products"
                       :total-excl="basket.totalExcl"
                       :vat-percentage="basket.vatPercentage"
                       :total-vat="basket.totalVat"
                       :kwantum-korting-excl="basket.kwantumKortingExcl" :dealer-row="basket.dealerRow"
                       :dealer-amount="basket.dealerAmount"
                       :totalIncl="basket.totalIncl" :shipping-costs="basket.shippingCostsExcl"
                       :is-pickup="hasPptMove" :show-shipping="true"></BasketSummary>
      </div>

    </div>

  </div>


</template>

<script>
import axios from "axios";
import ErrorBox from "../components/ErrorBox.vue";
import BasketSummary from "../components/BasketSummary.vue";
import TextInput from "../components/form/TextInput.vue";
import SelectInput from "../components/form/SelectInput.vue";
import RadioInput from "../components/form/RadioInput.vue";
import FormInput from "../components/form/FormInput.vue";
import CheckboxInput from "../components/form/CheckboxInput.vue";

export default {
  name: "DeliveryDetails",
  components: {CheckboxInput, FormInput, RadioInput, SelectInput, TextInput, BasketSummary, ErrorBox},
  data() {
    return {
      public: public_dir, //deze mag niet hetzelfde heten, anders pikt ie t niet in de productie build
      loading: true,
      showLoaderPopup: false,
      form: {},
      basket: {},
      contactForm: {},
      hasPptMove: false,
      disableNextStep: false,
      isAdmin: false,
      step: 2,
      totalSteps: 3,
      selectCountries: [],
      termsHref: document.getElementById('order_spa').dataset.termsHref,
      deliveryHref: document.getElementById('order_spa').dataset.deliveryHref,
    };
  },
  async mounted() {
    try {
      await this.getBasket();
      await this.getForm('contact');
      await this.getForm('delivery');
      await this.getSelectCountries();
    } catch (e) {
      this.$handleError(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async updateInputValue(fieldName, fieldValue) {
      this.form[fieldName] = fieldValue;
      await this.updateForm('delivery');
    },
    async getBasket() {

      try {
        const response = await axios.get(`/${this.$sessionLang}/api/getbasket`);

        this.basket = response.data.data.basket;
        this.hasPptMove = response.data.data.hasPptMove;
      } catch (error) {
        this.$handleError(error);
      }
    },
    async getForm(formStep) {
      try {
        const response = await axios.post(`/${this.$sessionLang}/api/getform`, {
          step: formStep,
        });

        if (!response.data.data.hasProducts) {
          this.$router.push({name: 'basket'});
          return;
        }

        if (formStep == 'delivery') {
          this.form = response.data.data.form;
        }
        else if (formStep == 'contact') {
          this.contactForm = response.data.data.form;
        }

        this.isAdmin = response.data.data.isAdmin;
        this.disableNextStep = (!this.form.firstVisit && this.hasErrors());
      } catch (error) {
        this.$handleError(error);
      }
    },
    async getSelectCountries() {
      try {
        const response = await axios.post(`/${this.$sessionLang}/api/getselectcountries`, {
          lang: this.$sessionLang,
        });

        this.selectCountries = response.data.data;

      } catch (error) {
        this.$handleError(error);
      }
    },
    async updateForm(formStep, recalculate = false) {
      try {
        await axios.post(`/${this.$sessionLang}/api/updateform`, {
          form: this.form,
          step: formStep,
        })

        if (recalculate) { // herberekenen (afleverland veranderd / afleveradres gelijk/afwijkend)
          await this.getBasket();
        }

        await this.getForm('delivery');

      } catch (error) {
        this.$handleError(error);
      }
    },
    hasErrors() {
      let hasErrors = false;
      if (this.form.errors && Object.keys(this.form.errors).length > 0) {
        hasErrors = true;
      }

      return hasErrors;
    },
    getTotalSteps() {
      let totalSteps = 3;
      if (this.hasPptMove) {
        totalSteps = 4;
      }

      return this.step + '/' + totalSteps;
    },
    async nextStep(step) {
      this.form.firstVisit = false;
      await this.updateForm('delivery');

      if (!this.hasErrors() && !this.loading) {
        await this.sendDataLayer('add_shipping_info');
        this.$router.push({name: step});
      }
      else {
        this.disableNextStep = true;
      }
    },
    async placeOrder() {
      this.disableNextStep = true;
      this.showLoaderPopup = true;

      this.form.firstVisit = false;
      await this.updateForm('delivery');

      if (!this.hasErrors()) {
        try {

          let response = await axios.post(`/${this.$sessionLang}/api/placeorder`, {
            paymentMethod: false,
            type: 'offerte',
          });

          if (response.data.data.redirect) {
            await this.sendDataLayer('add_shipping_info');
            window.location.href = response.data.data.redirect;
          }

        } catch (error) {
          this.$handleError(error);
        }
      }

      this.showLoaderPopup = false;
    },
    async setType(type) {
      try {
        const response = await axios.post(`/${this.$sessionLang}/api/settype`, {
          type: type,
        });

      } catch (error) {
        this.$handleError(error);
      }
    },
    async sendDataLayer(name) {
      try {
        const response = await axios.post(`/${this.$sessionLang}/api/sendDataLayer`, {
          name: name,
        })

        if (response?.data?.data?.dataLayer?.length > 0) {
          let dataLayer = window.dataLayer || [];
          dataLayer.push({"ecommerce": null});
          dataLayer.push(response.data.data.dataLayer);
        }
      } catch (error) {
        this.$handleError(error);
      }
    }
  },
}
</script>

<style scoped>
.loader-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8); /* Semi-transparent white background */
  z-index: 9999;
}

.title {
  margin-bottom: 1em;
}

h3 {
  font: var(--bold-22-28);
}

.step-details {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.line {
  background-color: var(--light-brown);
  height: 2px;
  border: none;
  opacity: 0.6;
  flex-grow: 1;
}

.step {
  font: var(--bold-10);
  color: var(--white);
  background-color: var(--black);
  padding: 5px;
  border-radius: 16px;
  text-align: center;
}

.double-input {
  display: flex;
  gap: 15px;
}

.double-input .form-input:not(.input-small) {
  width: 100%;
}

.form-input {
  font: var(--bold-16-20);
  line-height: 28px;
  margin-bottom: 15px;
}

.form-input input {
  padding: 8px;
  opacity: 1;
}

.form-input select {
  opacity: 1;
}

.form-input textarea {
  width: 100%;
  font: var(--medium-16);
  border: 1px solid #C8C8CC;
  border-radius: 8px;
  padding: 8px;
  opacity: 1;
  background: #FFFFFF 0 0 no-repeat padding-box;
  resize: vertical;
}

.form-input input:focus,
.form-input textarea:focus {
  outline-color: var(--brown);
}

.form-input.checkbox {
  display: flex;
  gap: 8px;
  font: var(--medium-16);
}

.form-input.checkbox input {
  max-width: 20px;
  max-height: 20px;
  color: #C9C9CC;
  accent-color: var(--brown);
}

.form-input.checkbox.txtsmall {
  font: var(--medium-14);
}

.form-input.radio {
  display: flex;
  border: 1px solid #C9C9CC;
  border-radius: 8px;
  padding: 14px;
  gap: 12px;
  flex-grow: 1;
}

.form-input.radio label {
  flex-grow: 1;
}

.form-input.radio input {
  max-width: 16px;
  accent-color: var(--brown);
}

.form-input.radio input:focus {
  background-color: var(--beige);
}

.grouped-radio-inputs {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.gsd-btn {
  width: 100%;
  font: var(--bold-16-20);
}

a.gsd-btn {
  border: none;
  display: flex;
  justify-content: center;
  gap: 5px;
  padding: 0;
}

a.gsd-btn:hover:not(:disabled) {
  background-color: inherit;
  border: inherit;
  text-decoration: none;
  color: var(--orange);
}

.gsd-btn-secondary {
  margin: 20px 0;
}

.form-input a {
  color: var(--brown);
  text-decoration: underline;
}

.form-input a:hover {
  color: var(--orange);
}

label.error {
  color: red;
}

input.error,
select.error {
  border: 1px solid red !important;
}

input.error:focus,
select.error {
  outline-color: red;
}

.error-msg {
  color: red;
  font: var(--bold-10);
  font-style: italic;
}

.error-notification {
  color: red;
  text-decoration: underline;
  margin: 15px 0;
}

.btn-split {
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  margin-bottom: 35px;
}

.nav-btns .gsd-btn {
  margin: 10px 0 0 0;
}

.desktop,
.block-steps,
.block-right {
  display: none;
}

.loader {
  display: block;
  width: 48px;
  height: 48px;
  margin: 150px auto;
  border-radius: 50%;
  position: relative;
  animation: rotate 1s linear infinite;
}

.loader::before,
.loader::after {
  content: "";
  box-sizing: border-box;
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 5px solid var(--orange);
  animation: prixClipFix 2s linear infinite;
}

.loader::after {
  transform: rotate3d(90, 90, 0, 180deg);
  border-color: var(--black);
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes prixClipFix {
  0% {
    clip-path: polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0);
  }
  50% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 0, 100% 0, 100% 0);
  }
  75%, 100% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
  }
}

@media (min-width: 1140px) {
  .grouped-radio-inputs {
    flex-direction: row;
    gap: 16px;
  }

  .title {
    font: var(--bold-40-52);
  }

  h3 {
    font: var(--bold-32);
  }

  .step-details {
    display: none;
  }

  .blocks {
    display: flex;
    gap: 32px;
  }

  .block-steps {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 55px;
    gap: 8px;
  }

  .line-vertical {
    background-color: var(--light-brown);
    width: 2px;
    border: none;
    opacity: 0.6;
    flex-grow: 1;
  }

  .block-form {
    flex-grow: 1;
  }

  .block-form > h3 {
    margin-bottom: 24px;
  }

  .block-form .summary {
    display: none;
  }

  .summary {
    margin-top: 0;
  }

  .desktop,
  .block-right {
    display: inline-block;
  }

  .block-right {
    min-width: 440px;
    position: sticky;
    top: 90px;
    margin-left: auto;
    align-self: flex-start;
  }

  .nav-btns {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
  }

  .nav-btns .gsd-btn {
    width: auto;
    margin: 20px 0 0 0;
  }

  .btn-split {
    flex-direction: row;
    margin-bottom: 0;
    gap: 8px;
  }

}

</style>